import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  Typography,
  Statistic,
  Progress,
  Tooltip,
  Timeline,
  Alert,
  Badge,
  Popconfirm,
  message,
  Steps,
  Descriptions,
  Divider
} from 'antd';
import { 
  SafetyCertificateOutlined, 
  WarningOutlined, 
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  FireOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const SafetyPage = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('incidents');

  // Mock data for safety incidents
  const mockIncidents = [
    {
      id: 1,
      title: 'Minor Equipment Collision',
      type: 'near_miss',
      severity: 'low',
      location: 'Block S2B8',
      date: '2024-01-15',
      reportedBy: 'Mike Brown',
      status: 'resolved',
      description: 'Two ADTs came within close proximity but no contact occurred.',
      actionTaken: 'Additional safety briefing conducted',
      followUpDate: '2024-01-20'
    },
    {
      id: 2,
      title: 'Slip and Fall',
      type: 'injury',
      severity: 'medium',
      location: 'Workshop Area',
      date: '2024-01-10',
      reportedBy: 'Sarah Johnson',
      status: 'investigating',
      description: 'Worker slipped on wet surface, minor bruising reported.',
      actionTaken: 'Area marked with warning signs',
      followUpDate: '2024-01-25'
    },
    {
      id: 3,
      title: 'Fire Hazard Identified',
      type: 'hazard',
      severity: 'high',
      location: 'Fuel Storage',
      date: '2024-01-05',
      reportedBy: 'Safety Team',
      status: 'resolved',
      description: 'Potential fire hazard identified near fuel storage area.',
      actionTaken: 'Fire extinguishers added, safety barriers installed',
      followUpDate: '2024-01-12'
    },
    {
      id: 4,
      title: 'PPE Non-Compliance',
      type: 'violation',
      severity: 'low',
      location: 'Crushing Plant',
      date: '2024-01-03',
      reportedBy: 'John Smith',
      status: 'resolved',
      description: 'Worker found without proper safety helmet.',
      actionTaken: 'Safety training conducted',
      followUpDate: '2024-01-10'
    }
  ];

  // Mock data for safety inspections
  const mockInspections = [
    {
      id: 1,
      type: 'daily',
      area: 'Block S2B8',
      date: '2024-01-30',
      inspector: 'Sarah Johnson',
      score: 95,
      status: 'completed',
      findings: 2,
      recommendations: 'Maintain current safety standards'
    },
    {
      id: 2,
      type: 'weekly',
      area: 'Equipment Yard',
      date: '2024-01-28',
      inspector: 'Safety Team',
      score: 88,
      status: 'completed',
      findings: 5,
      recommendations: 'Improve equipment parking organization'
    },
    {
      id: 3,
      type: 'monthly',
      area: 'Workshop',
      date: '2024-01-25',
      inspector: 'John Smith',
      score: 92,
      status: 'completed',
      findings: 3,
      recommendations: 'Update safety signage'
    },
    {
      id: 4,
      type: 'quarterly',
      area: 'Entire Site',
      date: '2024-01-20',
      inspector: 'External Auditor',
      score: 85,
      status: 'in_progress',
      findings: 8,
      recommendations: 'Pending final report'
    }
  ];

  useEffect(() => {
    fetchSafetyData();
  }, []);

  const fetchSafetyData = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIncidents(mockIncidents);
      setInspections(mockInspections);
      setLoading(false);
    }, 500);
  };

  const handleAddRecord = () => {
    form.resetFields();
    setEditingRecord(null);
    setModalVisible(true);
  };

  const handleEditRecord = (record) => {
    form.setFieldsValue(record);
    setEditingRecord(record);
    setModalVisible(true);
  };

  const handleDeleteRecord = (id, type) => {
    if (type === 'incident') {
      setIncidents(incidents.filter(incident => incident.id !== id));
    } else {
      setInspections(inspections.filter(inspection => inspection.id !== id));
    }
    message.success('Record deleted successfully');
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      if (editingRecord) {
        // Update existing
        if (activeTab === 'incidents') {
          setIncidents(incidents.map(incident => 
            incident.id === editingRecord.id ? { ...incident, ...values } : incident
          ));
        } else {
          setInspections(inspections.map(inspection => 
            inspection.id === editingRecord.id ? { ...inspection, ...values } : inspection
          ));
        }
        message.success('Record updated successfully');
      } else {
        // Add new
        const newRecord = {
          id: activeTab === 'incidents' ? incidents.length + 1 : inspections.length + 1,
          ...values
        };
        if (activeTab === 'incidents') {
          setIncidents([...incidents, newRecord]);
        } else {
          setInspections([...inspections, newRecord]);
        }
        message.success('Record added successfully');
      }
      setModalVisible(false);
    }).catch(err => {
      console.log('Validation failed:', err);
    });
  };

  const getSeverityTag = (severity) => {
    switch (severity) {
      case 'low':
        return <Tag color="green">Low</Tag>;
      case 'medium':
        return <Tag color="orange">Medium</Tag>;
      case 'high':
        return <Tag color="red">High</Tag>;
      default:
        return <Tag color="default">{severity}</Tag>;
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'resolved':
        return <Tag icon={<CheckCircleOutlined />} color="success">Resolved</Tag>;
      case 'investigating':
        return <Tag icon={<ClockCircleOutlined />} color="processing">Investigating</Tag>;
      case 'in_progress':
        return <Tag icon={<ClockCircleOutlined />} color="processing">In Progress</Tag>;
      case 'completed':
        return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getTypeTag = (type) => {
    const typeColors = {
      'near_miss': 'blue',
      'injury': 'red',
      'hazard': 'orange',
      'violation': 'purple',
      'daily': 'green',
      'weekly': 'blue',
      'monthly': 'orange',
      'quarterly': 'purple'
    };
    return <Tag color={typeColors[type] || 'default'}>{type.replace('_', ' ').toUpperCase()}</Tag>;
  };

  const incidentColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div><strong>{text}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.location} • {record.date}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => getTypeTag(type),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => getSeverityTag(severity),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Reported By',
      dataIndex: 'reportedBy',
      key: 'reportedBy',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditRecord(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this incident?"
            description="Are you sure you want to delete this incident record?"
            onConfirm={() => handleDeleteRecord(record.id, 'incident')}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const inspectionColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => getTypeTag(type),
    },
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector',
      key: 'inspector',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score) => (
        <div>
          <Progress 
            percent={score} 
            size="small" 
            status={score >= 90 ? 'success' : score >= 80 ? 'normal' : 'exception'}
          />
          <div style={{ fontSize: '12px', textAlign: 'center' }}>{score}/100</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Report">
            <Button 
              type="text" 
              icon={<FileTextOutlined />}
              onClick={() => handleViewReport(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditRecord(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this inspection?"
            description="Are you sure you want to delete this inspection record?"
            onConfirm={() => handleDeleteRecord(record.id, 'inspection')}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleViewDetails = (record) => {
    Modal.info({
      title: record.title,
      width: 600,
      content: (
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Type">{record.type}</Descriptions.Item>
          <Descriptions.Item label="Severity">{record.severity}</Descriptions.Item>
          <Descriptions.Item label="Location">{record.location}</Descriptions.Item>
          <Descriptions.Item label="Date">{record.date}</Descriptions.Item>
          <Descriptions.Item label="Reported By">{record.reportedBy}</Descriptions.Item>
          <Descriptions.Item label="Status">{record.status}</Descriptions.Item>
          <Descriptions.Item label="Description">{record.description}</Descriptions.Item>
          <Descriptions.Item label="Action Taken">{record.actionTaken}</Descriptions.Item>
          <Descriptions.Item label="Follow-up Date">{record.followUpDate}</Descriptions.Item>
        </Descriptions>
      )
    });
  };

  const handleViewReport = (record) => {
    Modal.info({
      title: `${record.type.toUpperCase()} Inspection Report - ${record.area}`,
      width: 600,
      content: (
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Inspection Type">{record.type}</Descriptions.Item>
          <Descriptions.Item label="Area">{record.area}</Descriptions.Item>
          <Descriptions.Item label="Date">{record.date}</Descriptions.Item>
          <Descriptions.Item label="Inspector">{record.inspector}</Descriptions.Item>
          <Descriptions.Item label="Score">
            <Progress 
              percent={record.score} 
              status={record.score >= 90 ? 'success' : record.score >= 80 ? 'normal' : 'exception'}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Findings">{record.findings}</Descriptions.Item>
          <Descriptions.Item label="Recommendations">{record.recommendations}</Descriptions.Item>
          <Descriptions.Item label="Status">{record.status}</Descriptions.Item>
        </Descriptions>
      )
    });
  };

  const safetyStats = [
    { title: 'Days Since Last Incident', value: 15, color: '#52c41a', icon: <SafetyCertificateOutlined /> },
    { title: 'Open Incidents', value: 2, color: '#faad14', icon: <WarningOutlined /> },
    { title: 'Safety Score', value: '92%', color: '#1890ff', icon: <CheckCircleOutlined /> },
    { title: 'Inspections This Month', value: 8, color: '#722ed1', icon: <FileTextOutlined /> },
  ];

  const recentActivities = [
    { time: '2024-01-30 14:30', action: 'Daily safety inspection completed', person: 'Sarah Johnson', status: 'success' },
    { time: '2024-01-30 10:15', action: 'Safety briefing conducted', person: 'John Smith', status: 'success' },
    { time: '2024-01-29 16:45', action: 'New safety equipment installed', person: 'Maintenance Team', status: 'success' },
    { time: '2024-01-29 09:30', action: 'Incident reported and logged', person: 'Mike Brown', status: 'warning' },
    { time: '2024-01-28 11:00', action: 'Safety audit completed', person: 'External Auditor', status: 'success' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>
            <SafetyCertificateOutlined style={{ marginRight: '12px' }} />
            Safety Management
          </Title>
          <Text type="secondary">Monitor incidents, inspections, and safety compliance</Text>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddRecord}
          >
            Report Incident
          </Button>
        </Col>
      </Row>

      {/* Safety Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {safetyStats.map(stat => (
          <Col xs={24} sm={12} md={6} key={stat.title}>
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Safety Alert */}
      <Alert
        message="Safety First!"
        description="Remember to always wear proper PPE, follow safety procedures, and report any hazards immediately."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Tabs for Incidents and Inspections */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  {activeTab === 'incidents' ? 'Safety Incidents' : 'Safety Inspections'}
                </span>
                <div>
                  <Button 
                    type={activeTab === 'incidents' ? 'primary' : 'default'}
                    onClick={() => setActiveTab('incidents')}
                    style={{ marginRight: '8px' }}
                  >
                    Incidents
                  </Button>
                  <Button 
                    type={activeTab === 'inspections' ? 'primary' : 'default'}
                    onClick={() => setActiveTab('inspections')}
                  >
                    Inspections
                  </Button>
                </div>
              </div>
            }
          >
            {activeTab === 'incidents' ? (
              <Table
                columns={incidentColumns}
                dataSource={incidents}
                loading={loading}
                rowKey="id"
                pagination={{ pageSize: 5 }}
              />
            ) : (
              <Table
                columns={inspectionColumns}
                dataSource={inspections}
                loading={loading}
                rowKey="id"
                pagination={{ pageSize: 5 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Activities and Safety Timeline */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Recent Safety Activities">
            <Timeline
              items={recentActivities.map((activity, index) => ({
                key: index,
                color: activity.status === 'success' ? 'green' : activity.status === 'warning' ? 'orange' : 'red',
                children: (
                  <div>
                    <div><strong>{activity.action}</strong></div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {activity.time} • {activity.person}
                    </div>
                  </div>
                )
              }))}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Safety Compliance Status">
            <Steps direction="vertical" current={1}>
              <Step 
                title="Daily Inspections" 
                description="Completed 28/30 days this month" 
                icon={<CheckCircleOutlined />}
                status="finish"
              />
              <Step 
                title="Safety Training" 
                description="Next session scheduled for Feb 5" 
                icon={<ClockCircleOutlined />}
                status="process"
              />
              <Step 
                title="Equipment Safety Checks" 
                description="Due for quarterly review" 
                icon={<ClockCircleOutlined />}
                status="wait"
              />
              <Step 
                title="Emergency Drills" 
                description="Last drill conducted Jan 15" 
                icon={<CheckCircleOutlined />}
                status="finish"
              />
            </Steps>
            <Divider />
            <Paragraph>
              <EnvironmentOutlined style={{ marginRight: '8px' }} />
              <strong>Environmental Safety:</strong> All environmental compliance checks are up to date.
            </Paragraph>
            <Paragraph>
              <TeamOutlined style={{ marginRight: '8px' }} />
              <strong>Personnel Training:</strong> 95% of staff have completed mandatory safety training.
            </Paragraph>
            <Paragraph>
              <FireOutlined style={{ marginRight: '8px' }} />
              <strong>Fire Safety:</strong> All fire extinguishers inspected and certified.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal
        title={editingRecord ? 'Edit Record' : 'Add New Record'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          name="safety_form"
        >
          {activeTab === 'incidents' ? (
            <>
              <Form.Item
                name="title"
                label="Incident Title"
                rules={[{ required: true, message: 'Please enter incident title' }]}
              >
                <Input placeholder="Enter incident title" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="Type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select placeholder="Select type">
                      <Option value="near_miss">Near Miss</Option>
                      <Option value="injury">Injury</Option>
                      <Option value="hazard">Hazard</Option>
                      <Option value="violation">Violation</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="severity"
                    label="Severity"
                    rules={[{ required: true, message: 'Please select severity' }]}
                  >
                    <Select placeholder="Select severity">
                      <Option value="low">Low</Option>
                      <Option value="medium">Medium</Option>
                      <Option value="high">High</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="location"
                    label="Location"
                    rules={[{ required: true, message: 'Please enter location' }]}
                  >
                    <Input placeholder="Enter location" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="date"
                    label="Date"
                    rules={[{ required: true, message: 'Please select date' }]}
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <Input.TextArea rows={3} placeholder="Enter incident description" />
              </Form.Item>
              <Form.Item
                name="actionTaken"
                label="Action Taken"
              >
                <Input.TextArea rows={2} placeholder="Enter action taken" />
              </Form.Item>
            </>
          ) : (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="Inspection Type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select placeholder="Select type">
                      <Option value="daily">Daily</Option>
                      <Option value="weekly">Weekly</Option>
                      <Option value="monthly">Monthly</Option>
                      <Option value="quarterly">Quarterly</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="area"
                    label="Area"
                    rules={[{ required: true, message: 'Please enter area' }]}
                  >
                    <Input placeholder="Enter area" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="date"
                    label="Date"
                    rules={[{ required: true, message: 'Please select date' }]}
                  >
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="inspector"
                    label="Inspector"
                    rules={[{ required: true, message: 'Please enter inspector name' }]}
                  >
                    <Input placeholder="Enter inspector name" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="score"
                label="Score (0-100)"
                rules={[
                  { required: true, message: 'Please enter score' },
                  { type: 'number', min: 0, max: 100, message: 'Score must be between 0 and 100' }
                ]}
              >
                <Input type="number" placeholder="Enter score" />
              </Form.Item>
              <Form.Item
                name="recommendations"
                label="Recommendations"
              >
                <Input.TextArea rows={2} placeholder="Enter recommendations" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SafetyPage;